import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import IconButton from 'LuxKit/Button/IconButton'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LineCalendarAltIcon from 'LuxKit/Icons/line/LineCalendarAltIcon'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import noop from 'lib/function/noop'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'

interface Props {
  onDelete?: () => void
  onAddToTrip?: () => void
  onAskAI?: () => void
}

function ChatItemDropdown(props: Props) {
  const intl = useIntl()
  const { onDelete = noop, onAddToTrip, onAskAI } = props
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending } = useItineraryContext()
  const actionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const toggleDropdown = useCallback(() => {
    if (actionsDisabled) return
    setIsDropdownOpen(prev => !prev)
  }, [actionsDisabled])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleDelete = useCallback(() => {
    if (actionsDisabled) return
    closeDropdown()
    onDelete()
  }, [actionsDisabled, closeDropdown, onDelete])

  const handleAddToTrip = useCallback(() => {
    closeDropdown()
    onAddToTrip?.()
  }, [closeDropdown, onAddToTrip])

  const handleAskAI = useCallback(() => {
    closeDropdown()
    onAskAI?.()
  }, [closeDropdown, onAskAI])

  return (
    <>
      <IconButton
        size="small"
        kind="tertiary"
        onClick={toggleDropdown}
        ref={dropdownTriggerRef}
        disabled={actionsDisabled}
      >
        <LineEllipsisVIcon />
      </IconButton>
      <DropdownList
        size="S"
        open={isDropdownOpen}
        onClose={closeDropdown}
        triggerRef={dropdownTriggerRef}
        anchorRef={dropdownTriggerRef}
        placement="bottom-end"
        portaled
      >
        {onAskAI && (
          <ClickableListItem
            title={intl.formatMessage({
              id: 'J4PzW_L1v7Op6leAVFO_',
              defaultMessage: 'Ask AI',
              description: 'Dropdown menu item to ask AI for suggestions about a trip day',
            })}
            size="medium"
            startIcon={<AISparkleIcon size="S" />}
            onClick={handleAskAI}
          />
        )}
        {onAddToTrip && (
          <ClickableListItem
            title={intl.formatMessage({
              id: 'sscpRc54E54WtBIXCi07',
              defaultMessage: 'Reschedule stay',
              description: 'Dropdown menu item to reschedule an accommodation stay in the itinerary',
            })}
            size="medium"
            startIcon={<LineCalendarAltIcon size="S" />}
            onClick={handleAddToTrip}
          />
        )}
        <ClickableListItem
          title={intl.formatMessage({
            id: 'e4B_Ms_37XNc8-2CIHTd',
            defaultMessage: 'Delete',
            description: 'Dropdown menu item to delete an item from the itinerary',
          })}
          size="medium"
          startIcon={<LineTrashAltIcon size="S" />}
          onClick={handleDelete}
        />
      </DropdownList>
    </>
  )
}

export default ChatItemDropdown
